import { useState, useCallback, useRef } from 'react'
import { Plus, X, Mic, Play, Pause,
         ChevronLeft, Edit3, Trash2, ZoomIn } from 'lucide-react'
import { format, parseISO, differenceInDays } from 'date-fns'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { useCapsules, useCreateCapsule } from '@/hooks/useData'
import { supabase, BUCKETS } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'
import { useQueryClient } from '@tanstack/react-query'
import ImageViewer from '@/components/ui/ImageViewer'
import type { Capsule } from '@/types/database'
import {
  getSupportedMimeType, getAudioExtension,
  formatBytes, MAX_VOICE_B, MAX_VOICE_MB,
} from '@/lib/mediaUtils'

const ICONS = ['🌟','💌','🎂','🏠','🎓','💍','🌍','🔮','🕯️','📚']

// ── Detect media type from URL ────────────────────────
function getMediaType(url: string): 'photo' | 'video' | 'voice' {
  const l = url.toLowerCase()
  // Check for audio file extensions and patterns first
  if (
    l.includes('.mp3') || l.includes('.m4a') || l.includes('.ogg') ||
    l.includes('.wav') || l.includes('voice-') ||
    (l.includes('.webm') && !l.includes('video')) ||
    l.includes('audio')
  ) return 'voice'
  if (
    l.includes('.mp4') || l.includes('.mov') || l.includes('.avi') ||
    l.includes('.mkv') || l.includes('video')
  ) return 'video'
  return 'photo'
}

// ── Robust Audio Player ───────────────────────────────
function AudioPlayer({ url }: { url: string }) {
  const [playing,  setPlaying]  = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [loaded,   setLoaded]   = useState(false)
  const [errored,  setErrored]  = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const toggle = async () => {
    const a = audioRef.current
    if (!a || errored) return
    try {
      if (playing) { a.pause(); setPlaying(false) }
      else { await a.play(); setPlaying(true) }
    } catch { setErrored(true) }
  }

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current
    if (!a) return
    a.currentTime = +e.target.value
    setProgress(+e.target.value)
  }

  const fmt = (s: number) =>
    isNaN(s) || !isFinite(s)
      ? '0:00'
      : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`

  if (errored) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
        <Mic size={16} className="text-purple flex-shrink-0" />
        <span className="text-xs text-white/40 flex-1">Voice note</span>
        <a href={url} target="_blank" rel="noreferrer"
          className="text-xs text-blue-light underline font-mono">
          Open in browser →
        </a>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-purple/10 border border-purple/20">
      {/* The audio element — multiple source formats for cross-browser compatibility */}
      <audio
        ref={audioRef}
        preload="auto"
        onCanPlay={() => setLoaded(true)}
        onLoadedMetadata={() => {
          setLoaded(true)
          setDuration(audioRef.current?.duration ?? 0)
        }}
        onTimeUpdate={() => {
          setProgress(audioRef.current?.currentTime ?? 0)
          setDuration(audioRef.current?.duration ?? 0)
        }}
        onEnded={() => { setPlaying(false); setProgress(0) }}
        onError={() => setErrored(true)}
      >
        {/* Try multiple source types so it works on all browsers */}
        <source src={url} type="audio/mp4" />
        <source src={url} type="audio/webm" />
        <source src={url} type="audio/ogg" />
        <source src={url} />
      </audio>

      {/* Play / Pause */}
      <button
        onClick={toggle}
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform active:scale-90"
        style={{ background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)' }}
      >
        {playing
          ? <Pause size={16} className="text-white" />
          : <Play  size={16} className="text-white ml-0.5" />}
      </button>

      {/* Scrubber */}
      <div className="flex-1 min-w-0">
        <input
          type="range"
          min={0}
          max={duration || 100}
          value={progress}
          onChange={seek}
          disabled={!loaded}
          className="w-full h-1.5 accent-purple rounded-full cursor-pointer disabled:opacity-40"
        />
        <div className="flex justify-between mt-1">
          <span className="font-mono text-[10px] text-white/30">{fmt(progress)}</span>
          <span className="font-mono text-[10px] text-white/30">
            {loaded ? fmt(duration) : '…'}
          </span>
        </div>
      </div>

      <Mic size={14} className="text-purple/60 flex-shrink-0" />
    </div>
  )
}

type NewMediaItem = {
  file: File
  preview: string
  type: 'photo' | 'video' | 'voice'
}

// ── Main Page ─────────────────────────────────────────
export default function CapsulesPage() {
  const { data: capsules } = useCapsules()
  const createCapsule      = useCreateCapsule()
  const { user }           = useAuthStore()
  const qc                 = useQueryClient()

  // Detail / edit
  const [selected,       setSelected]       = useState<Capsule | null>(null)
  const [editing,        setEditing]        = useState(false)
  const [confirmDelete,  setConfirmDelete]  = useState(false)
  const [viewerIndex,    setViewerIndex]    = useState<number | null>(null)

  // Create modal
  const [showCreate,  setShowCreate]  = useState(false)
  const [title,       setTitle]       = useState('')
  const [icon,        setIcon]        = useState('🌟')
  const [unlockDate,  setUnlockDate]  = useState('')
  const [message,     setMessage]     = useState('')
  const [newItems,    setNewItems]    = useState<NewMediaItem[]>([])
  const [uploading,   setUploading]   = useState(false)
  const [recording,   setRecording]   = useState(false)
  const [error,       setError]       = useState('')

  // Edit fields
  const [editTitle,   setEditTitle]   = useState('')
  const [editIcon,    setEditIcon]    = useState('🌟')
  const [editDate,    setEditDate]    = useState('')
  const [editMessage, setEditMessage] = useState('')
  const [editItems,   setEditItems]   = useState<NewMediaItem[]>([])
  const [removedUrls, setRemovedUrls] = useState<string[]>([])
  const [editSaving,  setEditSaving]  = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const timerRef         = useRef<ReturnType<typeof setInterval> | null>(null)
  const chunksRef        = useRef<BlobPart[]>([])
  const [recSeconds,     setRecSeconds] = useState(0)

  // ── Dropzone ──────────────────────────────────────────
  const makeDropzone = (forEdit: boolean) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useDropzone({
      onDrop: (files: File[]) => {
        const items: NewMediaItem[] = files.map(f => ({
          file: f,
          preview: URL.createObjectURL(f),
          type: f.type.startsWith('video') ? 'video' : 'photo',
        }))
        if (forEdit) setEditItems(prev => [...prev, ...items])
        else setNewItems(prev => [...prev, ...items])
      },
      accept: { 'image/*': [], 'video/*': [] },
      maxSize: 50 * 1024 * 1024,
    })

  const { getRootProps: getCRootProps, getInputProps: getCInputProps, isDragActive: isCDrag } = makeDropzone(false)
  const { getRootProps: getERootProps, getInputProps: getEInputProps } = makeDropzone(true)

  // ── Voice recording ───────────────────────────────────
  const startRecording = async (forEdit: boolean) => {
    try {
      const stream   = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {})
      chunksRef.current = []
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.onstop = () => {
        const actualMime = recorder.mimeType || mimeType || 'audio/webm'
        const ext  = getAudioExtension(actualMime)
        const blob = new Blob(chunksRef.current, { type: actualMime })
        if (blob.size > MAX_VOICE_B) {
          setError(`Voice note too large. Max ${MAX_VOICE_MB}MB.`)
          stream.getTracks().forEach(t => t.stop())
          return
        }
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: actualMime })
        const item: NewMediaItem = { file, preview: URL.createObjectURL(blob), type: 'voice' }
        if (forEdit) setEditItems(prev => [...prev, item])
        else setNewItems(prev => [...prev, item])
        stream.getTracks().forEach(t => t.stop())
      }
      recorder.start(100)
      mediaRecorderRef.current = recorder
      setRecording(true)
      setRecSeconds(0)
      timerRef.current = setInterval(() => setRecSeconds(s => s + 1), 1000)
    } catch {
      setError('Microphone access denied.')
    }
  }

  const stopRecording = () => {
    mediaRecorderRef.current?.stop()
    setRecording(false)
    mediaRecorderRef.current = null
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }

  const fmtSecs = (s: number) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`

  // ── Upload helper ─────────────────────────────────────
  const uploadItems = async (items: NewMediaItem[]): Promise<string[]> => {
    const urls: string[] = []
    for (const item of items) {
      const path = `${user!.id}/${Date.now()}-${item.file.name}`
      const { data, error: upErr } = await supabase.storage
        .from(BUCKETS.CAPSULES)
        .upload(path, item.file, { upsert: true, contentType: item.file.type })
      if (upErr) throw upErr
      const { data: { publicUrl } } = supabase.storage.from(BUCKETS.CAPSULES).getPublicUrl(data.path)
      urls.push(publicUrl)
    }
    return urls
  }

  // ── Create ────────────────────────────────────────────
  const resetCreate = () => {
    newItems.forEach(m => URL.revokeObjectURL(m.preview))
    setTitle(''); setIcon('🌟'); setUnlockDate(''); setMessage('')
    setNewItems([]); setError(''); setShowCreate(false)
  }

  const handleCreate = async () => {
    if (!title || !unlockDate) return
    setUploading(true); setError('')
    try {
      const mediaUrls = await uploadItems(newItems)
      await createCapsule.mutateAsync({
        title, icon,
        unlock_date: new Date(unlockDate).toISOString(),
        message: message || null,
        status: 'building',
        media_urls: mediaUrls,
        recipient_ids: [],
      })
      resetCreate()
    } catch (err: any) {
      setError(err.message ?? 'Failed to create capsule.')
    } finally {
      setUploading(false)
    }
  }

  // ── Open detail ───────────────────────────────────────
  const openDetail = (c: Capsule) => {
    setSelected(c); setEditing(false); setConfirmDelete(false)
    setEditItems([]); setRemovedUrls([])
  }

  // ── Start edit ────────────────────────────────────────
  const startEdit = () => {
    if (!selected) return
    setEditTitle(selected.title)
    setEditIcon(selected.icon)
    setEditDate(format(parseISO(selected.unlock_date), 'yyyy-MM-dd'))
    setEditMessage(selected.message ?? '')
    setEditItems([]); setRemovedUrls([])
    setEditing(true)
  }

  // ── Save edit ─────────────────────────────────────────
  const saveEdit = async () => {
    if (!selected || !editTitle || !editDate) return
    setEditSaving(true)
    try {
      const uploaded  = await uploadItems(editItems)
      const keptUrls  = selected.media_urls.filter(u => !removedUrls.includes(u))
      const finalUrls = [...keptUrls, ...uploaded]
      const { error: err } = await supabase.from('capsules').update({
        title:       editTitle,
        icon:        editIcon,
        unlock_date: new Date(editDate).toISOString(),
        message:     editMessage || null,
        media_urls:  finalUrls,
        updated_at:  new Date().toISOString(),
      }).eq('id', selected.id)
      if (err) throw err
      const updated = { ...selected, title: editTitle, icon: editIcon,
        unlock_date: new Date(editDate).toISOString(),
        message: editMessage || null, media_urls: finalUrls }
      qc.invalidateQueries({ queryKey: ['capsules', user?.id] })
      setSelected(updated); setEditing(false)
    } catch (err: any) {
      setError(err.message ?? 'Failed to save.')
    } finally {
      setEditSaving(false)
    }
  }

  // ── Delete ────────────────────────────────────────────
  const deleteCapsule = async () => {
    if (!selected) return
    await supabase.from('capsules').delete().eq('id', selected.id)
    qc.invalidateQueries({ queryKey: ['capsules', user?.id] })
    setSelected(null); setConfirmDelete(false)
  }

  // Separate URL arrays for the detail view
  const detailPhotos = selected?.media_urls.filter(u => getMediaType(u) === 'photo') ?? []
  const detailVideos = selected?.media_urls.filter(u => getMediaType(u) === 'video') ?? []
  const detailVoices = selected?.media_urls.filter(u => getMediaType(u) === 'voice') ?? []

  const MediaThumb = ({ url, onRemove }: { url: string; onRemove: () => void }) => {
    const mt = getMediaType(url)
    return (
      <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
        {mt === 'photo' ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : mt === 'video' ? (
          <Play size={20} className="text-white/40" />
        ) : (
          <Mic size={20} className="text-purple/60" />
        )}
        <button onClick={onRemove}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center">
          <X size={10} className="text-white" />
        </button>
      </div>
    )
  }

  // ── RENDER ────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-full">

      {/* Top bar */}
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-white">Time Capsules</h1>
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mt-0.5">
            Seal memories for the future
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#4f6ef7,#8b5cf6)' }}>
          <Plus size={18} className="text-white" />
        </button>
      </div>

      {/* Capsule list */}
      <div className="px-4 py-4 flex flex-col gap-3">
        {!capsules?.length ? (
          <div className="flex flex-col items-center justify-center text-center py-24 gap-5">
            <div className="text-6xl">⏳</div>
            <p className="font-serif text-2xl font-bold text-white/60 mb-1">Seal a memory</p>
            <p className="font-serif text-lg italic text-white/30">for someone you love.</p>
            <button onClick={() => setShowCreate(true)}
              className="mt-2 px-8 py-3.5 rounded-2xl font-semibold text-white text-sm"
              style={{ background: 'linear-gradient(135deg,#4f6ef7,#8b5cf6)' }}>
              Create First Capsule
            </button>
          </div>
        ) : capsules.map(c => {
          const days = differenceInDays(parseISO(c.unlock_date), new Date())
          const unlocked = days <= 0
          const photos = c.media_urls.filter(u => getMediaType(u) === 'photo')
          const videos = c.media_urls.filter(u => getMediaType(u) === 'video')
          const voices = c.media_urls.filter(u => getMediaType(u) === 'voice')
          return (
            <div key={c.id} onClick={() => openDetail(c)}
              className={`card p-4 cursor-pointer active:scale-[0.98] transition-all ${unlocked ? 'border-yellow-500/30' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="text-3xl">{c.icon}</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">{c.title}</h3>
                  <p className="font-mono text-xs text-white/30 mt-0.5">
                    {unlocked ? '🔓 Unlocked!' : `🔒 Unlocks ${format(parseISO(c.unlock_date), 'MMM d, yyyy')}`}
                  </p>
                </div>
                {!unlocked && (
                  <div className="text-right">
                    <div className="font-serif text-2xl font-bold" style={{ color: '#7b96ff' }}>{days}</div>
                    <div className="font-mono text-[9px] text-white/30">days left</div>
                  </div>
                )}
              </div>
              {c.message && (
                <p className="text-white/40 text-sm italic mt-3 border-t border-white/[0.07] pt-3 line-clamp-2">
                  "{c.message}"
                </p>
              )}
              {/* Media strip */}
              {c.media_urls.length > 0 && (
                <div className="flex gap-2 mt-3 overflow-x-auto">
                  {photos.slice(0,3).map((url,i) => (
                    <img key={i} src={url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-white/10" />
                  ))}
                  {videos.length > 0 && (
                    <div className="w-12 h-12 rounded-lg flex-shrink-0 bg-white/5 border border-white/10 flex items-center justify-center">
                      <Play size={16} className="text-white/40" />
                    </div>
                  )}
                  {voices.length > 0 && (
                    <div className="w-12 h-12 rounded-lg flex-shrink-0 bg-purple/10 border border-purple/20 flex items-center justify-center">
                      <Mic size={16} className="text-purple/60" />
                    </div>
                  )}
                </div>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                <span className={`text-xs px-2.5 py-1 rounded-full font-mono border
                  ${c.status==='sealed' ? 'border-yellow-500/30 text-yellow-400 bg-yellow-500/10' :
                    c.status==='unlocked' ? 'border-green-500/30 text-green-400 bg-green-500/10' :
                    'border-blue/30 text-blue-light bg-blue/10'}`}>{c.status}</span>
                {photos.length > 0 && <span className="text-xs px-2.5 py-1 rounded-full font-mono border border-white/10 text-white/30">📷 {photos.length}</span>}
                {videos.length > 0 && <span className="text-xs px-2.5 py-1 rounded-full font-mono border border-white/10 text-white/30">🎥 {videos.length}</span>}
                {voices.length > 0 && <span className="text-xs px-2.5 py-1 rounded-full font-mono border border-purple/20 text-purple/60">🎙 {voices.length}</span>}
                <span className="ml-auto text-xs text-white/20 font-mono">Tap to open →</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* ═══════════════════════════════════════
          DETAIL / EDIT VIEW
      ═══════════════════════════════════════ */}
      <AnimatePresence>
        {selected && (
          <motion.div className="fixed inset-0 z-50 bg-bg flex flex-col overflow-y-auto"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }} transition={{ duration: 0.22 }}>

            {/* Detail top bar */}
            <div className="glass sticky top-0 z-10 px-5 py-4 safe-top flex items-center justify-between">
              <button onClick={() => { setSelected(null); setEditing(false) }}
                className="flex items-center gap-1 text-white/50">
                <ChevronLeft size={20} /><span className="text-sm">Back</span>
              </button>
              {!editing ? (
                <div className="flex gap-2">
                  <button onClick={startEdit}
                    className="w-9 h-9 rounded-xl bg-blue/10 border border-blue/20 flex items-center justify-center">
                    <Edit3 size={16} className="text-blue-light" />
                  </button>
                  <button onClick={() => setConfirmDelete(true)}
                    className="w-9 h-9 rounded-xl bg-rose/10 border border-rose/20 flex items-center justify-center">
                    <Trash2 size={16} className="text-rose" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-4 items-center">
                  <button onClick={() => setEditing(false)} className="text-white/40 text-sm">Cancel</button>
                  <button onClick={saveEdit} disabled={editSaving}
                    className="text-blue-light font-semibold text-sm disabled:opacity-40">
                    {editSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {/* ── VIEW MODE ── */}
            {!editing && (
              <div className="px-5 py-6 flex flex-col gap-5">

                {/* Header */}
                <div className="flex items-center gap-4">
                  <div className="text-5xl">{selected.icon}</div>
                  <div className="flex-1">
                    <h1 className="font-serif text-2xl font-bold text-white">{selected.title}</h1>
                    <p className="font-mono text-xs text-white/30 mt-1">
                      {differenceInDays(parseISO(selected.unlock_date), new Date()) <= 0
                        ? '🔓 This capsule has unlocked'
                        : `🔒 Unlocks ${format(parseISO(selected.unlock_date), 'MMMM d, yyyy')}`}
                    </p>
                  </div>
                </div>

                {/* Countdown */}
                {differenceInDays(parseISO(selected.unlock_date), new Date()) > 0 && (() => {
                  const totalDays = differenceInDays(parseISO(selected.unlock_date), new Date())
                  const years  = Math.floor(totalDays / 365)
                  const months = Math.floor((totalDays % 365) / 30)
                  const days   = totalDays % 30
                  return (
                    <div className="flex gap-3">
                      {[{val:years,label:'Years'},{val:months,label:'Months'},{val:days,label:'Days'}].map(({val,label}) => (
                        <div key={label} className="flex-1 text-center p-3 rounded-xl bg-white/5 border border-white/[0.07]">
                          <div className="font-serif text-3xl font-bold text-white">{val}</div>
                          <div className="font-mono text-[10px] text-white/30 uppercase tracking-widest">{label}</div>
                        </div>
                      ))}
                    </div>
                  )
                })()}

                {/* Message */}
                {selected.message && (
                  <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/[0.07]">
                    <p className="font-mono text-[10px] text-white/25 tracking-widest uppercase mb-2">Personal Message</p>
                    <p className="font-serif text-base text-white/70 italic leading-relaxed">"{selected.message}"</p>
                  </div>
                )}

                {/* ── PHOTOS ── */}
                {detailPhotos.length > 0 && (
                  <div>
                    <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-3">
                      📷 Photos ({detailPhotos.length})
                    </p>
                    <div className={`grid gap-1 ${detailPhotos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {detailPhotos.map((url, i) => (
                        <div key={url}
                          onClick={() => setViewerIndex(i)}
                          className={`relative overflow-hidden rounded-xl cursor-zoom-in active:opacity-80 group
                            ${detailPhotos.length === 1 ? 'h-72' : 'h-44'}
                            ${i === 0 && detailPhotos.length === 3 ? 'col-span-2 h-56' : ''}`}>
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-60 transition-opacity" />
                          </div>
                          <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm rounded-full px-2 py-0.5 font-mono text-[9px] text-white/60">
                            Tap to expand
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── VIDEOS ── */}
                {detailVideos.length > 0 && (
                  <div>
                    <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-3">
                      🎥 Videos ({detailVideos.length})
                    </p>
                    <div className="flex flex-col gap-3">
                      {detailVideos.map((url, i) => (
                        <div key={i} className="rounded-xl overflow-hidden bg-black border border-white/10">
                          <video
                            src={url}
                            controls
                            playsInline
                            preload="metadata"
                            className="w-full max-h-72"
                            style={{ display: 'block' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── VOICE NOTES ── */}
                {detailVoices.length > 0 && (
                  <div>
                    <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-3">
                      🎙 Voice Notes ({detailVoices.length})
                    </p>
                    <div className="flex flex-col gap-2">
                      {detailVoices.map((url, i) => (
                        <AudioPlayer key={`${url}-${i}`} url={url} />
                      ))}
                    </div>
                  </div>
                )}

                {selected.media_urls.length === 0 && (
                  <div className="text-center py-8 text-white/20 font-serif text-lg italic">
                    No media in this capsule yet.
                  </div>
                )}

                <button onClick={startEdit}
                  className="w-full py-3.5 rounded-2xl border border-blue/20 bg-blue/5 text-blue-light font-semibold text-sm flex items-center justify-center gap-2">
                  <Edit3 size={16} /> Edit This Capsule
                </button>
              </div>
            )}

            {/* ── EDIT MODE ── */}
            {editing && (
              <div className="px-5 py-5 flex flex-col gap-4 pb-24">
                <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                  placeholder="Capsule title" className="input-field text-base font-semibold" />

                <div>
                  <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Icon</p>
                  <div className="flex gap-2 flex-wrap">
                    {ICONS.map(i => (
                      <button key={i} onClick={() => setEditIcon(i)}
                        className={`w-11 h-11 rounded-xl text-2xl transition-all
                          ${editIcon===i ? 'bg-blue/20 border-2 border-blue scale-110' : 'bg-white/5 border border-white/10'}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Unlock Date</p>
                  <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className="input-field" />
                </div>

                <div>
                  <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Personal Message</p>
                  <textarea value={editMessage} onChange={e => setEditMessage(e.target.value)}
                    placeholder="A message for when this capsule opens…"
                    className="input-field resize-none" rows={3} />
                </div>

                {/* Existing media */}
                {selected.media_urls.filter(u => !removedUrls.includes(u)).length > 0 && (
                  <div>
                    <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Current Items</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.media_urls.filter(u => !removedUrls.includes(u)).map(url => (
                        <MediaThumb key={url} url={url} onRemove={() => setRemovedUrls(prev => [...prev, url])} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Add more */}
                <div>
                  <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Add More Media</p>
                  <div {...getERootProps()}
                    className="border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer border-white/10 bg-white/[0.02] mb-2">
                    <input {...getEInputProps()} />
                    <p className="text-white/30 text-sm">📷 Add photos or videos</p>
                  </div>
                  <button onClick={() => recording ? stopRecording() : startRecording(true)}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl border text-sm font-medium transition-all
                      ${recording ? 'border-rose/40 bg-rose/10 text-rose animate-pulse' : 'border-purple/20 bg-purple/5 text-purple'}`}>
                    <Mic size={16} />
                    {recording ? `⏹ Stop — ${fmtSecs(recSeconds)}` : '🎙 Record Voice Note'}
                  </button>
                  {editItems.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editItems.map((item, i) => (
                        <MediaThumb key={i} url={item.preview}
                          onRemove={() => setEditItems(prev => prev.filter((_,idx) => idx !== i))} />
                      ))}
                    </div>
                  )}
                </div>

                {error && <p className="text-rose text-sm bg-rose/10 border border-rose/20 rounded-xl px-4 py-2">{error}</p>}
              </div>
            )}

            {/* Delete confirmation */}
            <AnimatePresence>
              {confirmDelete && (
                <motion.div className="fixed inset-0 bg-black/70 z-20 flex items-end"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => setConfirmDelete(false)}>
                  <motion.div className="w-full bg-surface rounded-t-3xl p-6 safe-bottom"
                    initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
                    onClick={e => e.stopPropagation()}>
                    <h3 className="font-serif text-2xl font-bold text-white text-center mb-2">Delete this capsule?</h3>
                    <p className="text-white/40 text-sm text-center mb-6 leading-relaxed">
                      All contents will be permanently deleted and cannot be recovered.
                    </p>
                    <div className="flex flex-col gap-3">
                      <button onClick={deleteCapsule}
                        className="w-full py-4 rounded-2xl font-semibold text-rose border border-rose/30 bg-rose/10">
                        Yes, Delete Forever
                      </button>
                      <button onClick={() => setConfirmDelete(false)} className="btn-secondary">
                        Cancel — Keep This Capsule
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════
          CREATE MODAL
      ═══════════════════════════════════════ */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end" onClick={resetCreate}>
          <div className="bg-surface w-full rounded-t-3xl safe-bottom overflow-y-auto"
            style={{ maxHeight: '92dvh' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <h3 className="font-serif text-2xl font-bold text-white">New Time Capsule</h3>
              <button onClick={resetCreate}><X size={20} className="text-white/40" /></button>
            </div>
            <div className="px-6 pb-8 flex flex-col gap-4">
              <input placeholder="Capsule title…" value={title}
                onChange={e => setTitle(e.target.value)} className="input-field" autoFocus />

              <div>
                <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Icon</p>
                <div className="flex gap-2 flex-wrap">
                  {ICONS.map(i => (
                    <button key={i} onClick={() => setIcon(i)}
                      className={`w-11 h-11 rounded-xl text-2xl transition-all
                        ${icon===i ? 'bg-blue/20 border-2 border-blue scale-110' : 'bg-white/5 border border-white/10'}`}>
                      {i}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Unlock Date</p>
                <input type="date" value={unlockDate}
                  onChange={e => setUnlockDate(e.target.value)}
                  min={format(new Date(),'yyyy-MM-dd')} className="input-field" />
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[{label:'1 Year',y:1},{label:'5 Years',y:5},{label:'10 Years',y:10},{label:'18 Years',y:18}].map(p => {
                    const d = new Date(); d.setFullYear(d.getFullYear()+p.y)
                    const v = format(d,'yyyy-MM-dd')
                    return (
                      <button key={p.label} onClick={() => setUnlockDate(v)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                          ${unlockDate===v ? 'border-blue text-blue-light bg-blue/10' : 'border-white/10 text-white/40 bg-white/5'}`}>
                        {p.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div>
                <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Personal Message</p>
                <textarea placeholder="A message for when this capsule opens…" value={message}
                  onChange={e => setMessage(e.target.value)} className="input-field resize-none" rows={3} />
              </div>

              <div>
                <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">Add Photos, Videos &amp; Voice Notes</p>
                <div {...getCRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition-all mb-3
                    ${isCDrag ? 'border-blue bg-blue/5' : 'border-white/10 bg-white/[0.02]'}`}>
                  <input {...getCInputProps()} />
                  <p className="text-white/40 text-sm">📷 Tap to add photos or videos</p>
                </div>
                <button onClick={() => recording ? stopRecording() : startRecording(false)}
                  className={`w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl border font-semibold text-sm transition-all
                    ${recording ? 'border-rose/40 bg-rose/10 text-rose animate-pulse' : 'border-purple/20 bg-purple/5 text-purple'}`}>
                  <Mic size={18} />
                  {recording ? `⏹ Stop — ${fmtSecs(recSeconds)}` : '🎙 Record Voice Note'}
                </button>
                {newItems.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {newItems.map((item, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                        {item.type === 'photo'
                          ? <img src={item.preview} alt="" className="w-full h-full object-cover" />
                          : item.type === 'video'
                          ? <Play size={20} className="text-white/40" />
                          : <Mic size={20} className="text-purple/60" />}
                        <button onClick={() => {
                          URL.revokeObjectURL(item.preview)
                          setNewItems(prev => prev.filter((_,idx) => idx !== i))
                        }} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center">
                          <X size={10} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && <p className="text-rose text-sm bg-rose/10 border border-rose/20 rounded-xl px-4 py-2">{error}</p>}

              <button onClick={handleCreate}
                disabled={uploading || createCapsule.isPending || !title || !unlockDate}
                className="btn-primary">
                {uploading ? 'Uploading…'
                  : createCapsule.isPending ? 'Creating…'
                  : `🔒 Seal Capsule${newItems.length > 0 ? ` (${newItems.length} item${newItems.length!==1?'s':''})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full screen photo viewer */}
      <AnimatePresence>
        {viewerIndex !== null && selected && (
          <ImageViewer
            urls={detailPhotos}
            startIndex={viewerIndex}
            onClose={() => setViewerIndex(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
